// Quote model matching backend structure v2.0

import { QuoteClauseResponse } from './quote-clause.model';

/**
 * Vista de una firma estampada en el documento legal de la cotización (V72).
 * El portal devuelve TODAS las firmas: filtrar por signerType para separar
 * "Firma del Responsable" (APPROVER) de "Firma del Cliente" (CLIENT).
 */
export interface QuoteSignatureView {
  signerType: 'CLIENT' | 'APPROVER';
  signatoryName: string;
  signatoryRole?: string | null;
  signatoryEmail?: string | null;
  signatoryIp?: string | null;
  signatureData?: string | null;
  signatureType?: string | null;
  documentHash?: string | null;
  datetimeSigned: string;
}

export interface QuoteItemRequest {
  itemOrder?: number;
  itemName: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  discountPercent?: number;
  discountAmount?: number;
  discountType?: 'percent' | 'fixed';
  unit?: string;
  itemTaxRate?: number;
  /** Código del ítem (V75). Opcional, máx 100. Si es producto y va vacío, el backend lo autocompleta con el sku. */
  itemCode?: string;
  /** Categoría del ítem (V76). Opcional, máx 150. Si es producto y va null, el backend la autocompleta con la categoría del producto. */
  category?: string | null;
  productId?: number;
  /**
   * Tipo de ítem. Valores ofrecidos en la UI: 'product' (Productos), 'service_resource'
   * (Servicios → Recursos) y 'service_consulting' (Servicios → Consultoría).
   * 'custom' = ítem libre; 'bundle' = legacy (ya no se ofrece, aceptado por compatibilidad).
   */
  itemType?: 'product' | 'service_resource' | 'service_consulting' | 'custom' | 'bundle';
  bundleId?: number;
  /**
   * (INC33) Variante del producto elegida. Solo válida con `itemType='product'` y debe pertenecer
   * a `productId` (el backend responde 400 si no). Cuando viaja con `unitPrice: 0` el backend
   * resuelve el precio efectivo de la variante y, si `itemCode` va vacío, su SKU.
   */
  variantId?: number | null;
  /**
   * (INC32) Regla de descuento aplicada a la línea. SOLO trazabilidad: el backend no la usa para
   * calcular — el descuento efectivo debe viajar siempre en `discountAmount`.
   */
  discountRuleId?: number | null;
}

/** Tipo de ítem de cotización (incluye legacy 'bundle'). */
export type QuoteItemType = 'product' | 'service_resource' | 'service_consulting' | 'custom' | 'bundle';

export interface QuoteItemResponse {
  id: number;
  idQuote: number;
  itemOrder?: number;
  itemName: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  discountPercent?: number;
  discountAmount?: number;
  discountType?: string;
  unit?: string;
  itemTaxRate?: number;
  /** Código del ítem (V75). Siempre resuelto en response (sku para productos). */
  itemCode?: string;
  /** Categoría del ítem (V76). Resuelta en response (categoría del producto para productos). */
  category?: string | null;
  subtotal: number;
  productId?: number;
  itemType?: string;
  bundleId?: number;
  /** (INC33) Variante del producto congelada en la línea. */
  variantId?: number | null;
  /** (INC33) Nombre de la variante resuelto del catálogo para mostrar (null si fue borrada). */
  variantName?: string | null;
  /** (INC33) SKU de la variante resuelto del catálogo (itemCode trae el SKU congelado al guardar). */
  variantSku?: string | null;
  /** (INC32) Regla de descuento aplicada (trazabilidad; no interviene en el cálculo). */
  discountRuleId?: number | null;
  datetimeCreated: string;
}

export interface QuoteRequest {
  dealId: number;
  quoteStatusId?: number;
  /** @deprecated Use datetimeExpires instead */
  validUntil?: string;
  datetimeExpires?: string;
  datetimeIssued?: string;
  notes?: string;
  termsConditions?: string;
  /** Notas internas: solo visibles para el equipo del CRM, nunca para el cliente. Máx 5000. */
  internalNotes?: string | null;
  /** Mensaje de apertura visible al cliente (portal, PDF y correo). Máx 2000. */
  openingMessage?: string | null;
  /**
   * Requiere aprobación interna antes de publicar/enviar (el backend devuelve 422 en Borrador).
   * Create: default false si se omite. Update: si se omite NO modifica el valor actual —
   * el form siempre lo envía explícito como boolean.
   */
  requiresApproval?: boolean;
  /** @deprecated Use globalDiscount + globalDiscountType instead */
  discount?: number;
  globalDiscount?: number;
  globalDiscountType?: 'percent' | 'fixed';
  currencyId?: number;
  taxEnabled?: boolean;
  taxRate?: number;
  taxLabel?: string;
  versionNotes?: string;
  idContactPrimary?: number;
  idTemplate?: number;
  /**
   * Cláusulas legales seleccionadas (V72). null = no toca; lista = reemplaza el conjunto
   * (el orden define el sortOrder). Alternativa a PUT /api/quotes/{id}/clauses.
   */
  clauseIds?: number[] | null;
  // ---- Condiciones comerciales (V74) — todas opcionales; en update, null = no tocar ----
  /** Forma de pago: 'CONTADO' | 'CREDITO'. */
  paymentType?: QuotePaymentType | null;
  /** Plazo de crédito en días (solo si paymentType = 'CREDITO'). */
  creditTermDays?: number | null;
  /** Anticipo en porcentaje (0–100). */
  advancePercent?: number | null;
  /**
   * Tasa de interés / financiamiento en porcentaje (0–100). Se aplica al saldo financiado
   * cuando el pago es a CRÉDITO. Opcional; null o 0 = sin interés.
   */
  interestRatePercent?: number | null;
  /** Plazo de entrega (texto libre). */
  deliveryTime?: string | null;
  /** Garantía (texto libre). */
  warranty?: string | null;
  /** Lugar de entrega (texto libre). */
  deliveryPlace?: string | null;
  /** Tipo de cambio editable (distinto del snapshot histórico). */
  exchangeRate?: number | null;
  /**
   * (V188) Moneda DESTINO del tipo de cambio: la moneda en la que se expresa el total
   * convertido del documento. Viaja junto a `exchangeRate` cuando la tasa se elige del
   * catálogo; null cuando se teclea a mano (el documento imprime solo la tasa).
   */
  exchangeRateCurrencyId?: number | null;
  // ---- Lista de precios y supervisor (V75) — opcionales ----
  /** Lista de precios ligada a la cotización (V75). null/omitir = sin lista. */
  priceListId?: number | null;
  /** Supervisor asignado a la cotización (usuario activo del negocio, V75). */
  supervisorUserId?: number | null;
  // ---- Opciones de presentación del documento (V76) — en update, null = no tocar ----
  /** Agrupar los ítems por categoría en el PDF/portal. Default false. */
  groupByCategory?: boolean | null;
  /** Mostrar la columna de códigos internos en el documento. Default true. */
  showItemCodes?: boolean | null;
  /**
   * @deprecated INC_CRM_005 (5) inc40 — "Permitir edición de precios en PDF" nunca tuvo efecto:
   * el generador de PDF lee la columna pero no cambia nada con ella. El control se retiró del
   * paso 3 (Totales) y el formulario ya NO envía este campo; el backend deja el valor almacenado
   * intacto cuando llega null. No volver a exponerlo en la UI sin implementar el comportamiento.
   */
  allowPriceEdit?: boolean | null;
  businessId: number;
  items: QuoteItemRequest[];
}

/** Forma de pago de la cotización (V74). */
export type QuotePaymentType = 'CONTADO' | 'CREDITO';

export interface QuoteResponse {
  id: number;
  dealId: number;
  quoteStatusId: number;
  statusName: string;
  quoteNumber: string;
  version: number;
  subtotal: number;
  tax: number;
  discount: number;
  totalAmount: number;
  /** @deprecated Use datetimeExpires instead */
  validUntil?: string;
  datetimeExpires?: string;
  datetimeIssued?: string;
  datetimeExpired?: string;
  notes?: string;
  termsConditions?: string;
  /** Notas internas: solo equipo CRM. El backend garantiza que NUNCA llega al portal público ni al PDF. */
  internalNotes?: string | null;
  /** Mensaje de apertura visible al cliente (portal, PDF y correo). */
  openingMessage?: string | null;
  /** true si requiere aprobación interna antes de publicarse o enviarse (en Borrador). */
  requiresApproval?: boolean;
  currencyId?: number;
  currencyCode?: string;
  currencySymbol?: string;
  exchangeRateSnapshot?: number;
  taxEnabled?: boolean;
  taxRate?: number;
  taxLabel?: string;
  globalDiscount?: number;
  globalDiscountType?: string;
  /** UUID del enlace público. Solo viene con valor en la respuesta de publish/rotate; null en el resto. */
  publicToken?: string;
  /** Portal público (JSON). Solo en la respuesta de publish/rotate; null en el resto. Capturar al momento. */
  publicUrl?: string;
  /** Descarga PDF pública (sin login). Solo en la respuesta de publish/rotate; null en el resto. Capturar al momento. */
  publicPdfUrl?: string;
  /** true si la cotización tiene un enlace público activo. Siempre presente. Usar para decidir botones. */
  hasPublicLink?: boolean;
  versionNotes?: string;
  idContactPrimary?: number;
  idTemplate?: number;
  /** Motivo catalogado de rechazo del cliente (null si no fue rechazada). */
  rejectionReason?: QuoteRejectionReason | null;
  /** Comentario libre del cliente al rechazar (null si no fue rechazada). */
  rejectionComment?: string | null;
  companyCommercialName?: string;
  contactFullName?: string;
  contactEmail?: string;
  /** Teléfono del contacto primario (nuevo — viene de v_quote_full_details) */
  contactPhone?: string;
  sellerFullName?: string;
  sellerEmail?: string;
  templateLogoUrl?: string;
  templatePrimaryColor?: string;
  /** (INC_CRM_005 (5) inc39) Color secundario de la plantilla: acentos de apoyo del documento. */
  templateSecondaryColor?: string;
  templateHeaderText?: string;
  /** (INC31) Pie de página configurado en la plantilla. */
  templateFooterText?: string | null;
  /** (INC31) Bloque de contacto del proveedor definido en la plantilla (puede ser multilínea). */
  templateContactInfo?: string | null;
  /** (INC31) Términos por defecto de la plantilla. Se usan si `termsConditions` viene vacío (igual que el PDF). */
  templateDefaultTerms?: string | null;
  /**
   * (INC29/30) Id del archivo del logo de la plantilla — MISMA fuente que embebe el PDF.
   * La URL se arma relativa: `${environment.apiUrl}api/files/${id}/download`.
   * Tiene precedencia sobre `providerLogoUrl` y `templateLogoUrl` (esta última es absoluta y
   * quedó congelada en el upload: se rompe si APP_BASE_URL apunta a otro host).
   */
  templateLogoFileId?: number | null;
  userCreatedId: number;
  businessId: number;
  datetimeCreated: string;
  datetimeUpdated?: string;
  datetimeSent?: string;
  datetimeViewed?: string;
  datetimeApproved?: string;
  datetimeRejected?: string;
  // ---- Documento legal (V72) ----
  providerName?: string;
  providerTaxId?: string;
  providerAddress?: string;
  providerPhone?: string;
  providerLogoUrl?: string;
  clientCompanyName?: string;
  clientTaxId?: string;
  clientAddress?: string;
  clientContactName?: string;
  clientEmail?: string;
  clientPhone?: string;
  validityDays?: number | null;
  currencyLabel?: string | null;
  /** Cláusulas legales incluidas en el documento. */
  clauses?: QuoteClauseResponse[];
  /** Firmas estampadas (APPROVER y CLIENT). */
  signatures?: QuoteSignatureView[];
  /** Hash SHA-256 del documento aceptado (null si aún no se firmó). */
  documentHash?: string | null;
  // ---- Condiciones comerciales (V74) ----
  paymentType?: QuotePaymentType | null;
  /** Etiqueta legible de la forma de pago ('Contado' | 'Crédito'). */
  paymentTypeLabel?: string | null;
  creditTermDays?: number | null;
  advancePercent?: number | null;
  /** Tasa de interés / financiamiento en porcentaje (0–100). null o 0 = sin interés. */
  interestRatePercent?: number | null;
  deliveryTime?: string | null;
  warranty?: string | null;
  deliveryPlace?: string | null;
  exchangeRate?: number | null;
  // ---- Moneda de conversión del tipo de cambio (V188) ----
  /** Moneda DESTINO del tipo de cambio. null = tasa tecleada a mano. */
  exchangeRateCurrencyId?: number | null;
  /** Código de la moneda de conversión, resuelto del catálogo (ej. 'USD'). */
  exchangeRateCurrencyCode?: string | null;
  /** Nombre de la moneda de conversión (ej. 'Dólar Estadounidense'). */
  exchangeRateCurrencyName?: string | null;
  /** Símbolo de la moneda de conversión (ej. '$'). */
  exchangeRateCurrencySymbol?: string | null;
  // ---- Lista de precios y supervisor (V75) ----
  priceListId?: number | null;
  priceListName?: string | null;
  supervisorUserId?: number | null;
  supervisorName?: string | null;
  /** Probabilidad de cierre (0–100), READ-ONLY del deal. null si no hay deal/probabilidad. */
  dealProbability?: number | null;
  // ---- Opciones de presentación del documento (V76) ----
  /** Agrupar los ítems por categoría en el PDF/portal. */
  groupByCategory?: boolean;
  /** Mostrar la columna de códigos internos en el documento. */
  showItemCodes?: boolean;
  /** Permitir edición de precios en el PDF (preferencia almacenada). */
  allowPriceEdit?: boolean;
  items: QuoteItemResponse[];
}

// Search/filter parameters
export interface QuoteSearchParams {
  businessId: number;
  dealId?: number;
  statusId?: number;
  dateFrom?: string;
  dateTo?: string;
  validFrom?: string;
  validTo?: string;
  searchQuoteNumber?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: string;
}

// Add bundle request
export interface AddBundleRequest {
  bundleId: number;
  quantity: number;
}

// Page response
export interface PageResponse<T = unknown> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Public portal response (no JWT required)
export interface QuotePublicResponse {
  quoteNumber: string;
  version: number;
  statusName: string;
  companyName?: string;
  contactName?: string;
  sellerName?: string;
  subtotal: number;
  tax: number;
  taxLabel?: string;
  taxRate?: number;
  taxEnabled?: boolean;
  globalDiscount?: number;
  globalDiscountType?: string;
  totalAmount: number;
  currencyCode?: string;
  currencySymbol?: string;
  datetimeIssued?: string;
  datetimeExpires?: string;
  notes?: string;
  termsConditions?: string;
  /** Mensaje de apertura del vendedor — se muestra al cliente al inicio de la cotización. */
  openingMessage?: string | null;
  templateLogoUrl?: string;
  templatePrimaryColor?: string;
  /** (INC_CRM_005 (5) inc39) Color secundario de la plantilla: acentos de apoyo del documento. */
  templateSecondaryColor?: string;
  templateHeaderText?: string;
  templateFooterText?: string;
  /** (INC31) Bloque de contacto del proveedor definido en la plantilla (puede ser multilínea). */
  templateContactInfo?: string | null;
  /** (INC31) Términos por defecto de la plantilla. Se usan si `termsConditions` viene vacío. */
  templateDefaultTerms?: string | null;
  /** (INC29/30) Id del archivo del logo de la plantilla. `GET /api/files/{id}/download` es público. */
  templateLogoFileId?: number | null;
  /** true si la cotización ya fue aceptada y firmada por el cliente. */
  signed?: boolean;
  /** Nombre de quien firmó (null si no está firmada). */
  signerName?: string | null;
  /** Fecha/hora de la firma en ISO UTC (null si no está firmada). */
  signedAt?: string | null;
  /** Motivo catalogado de rechazo (null si no fue rechazada). */
  rejectionReason?: QuoteRejectionReason | null;
  /** Comentario libre del cliente al rechazar (null si no fue rechazada). */
  rejectionComment?: string | null;
  // ---- Documento legal (V72) ----
  providerName?: string;
  providerTaxId?: string;
  providerAddress?: string;
  providerPhone?: string;
  providerLogoUrl?: string;
  clientCompanyName?: string;
  clientTaxId?: string;
  clientAddress?: string;
  clientContactName?: string;
  clientEmail?: string;
  clientPhone?: string;
  /** Período de validez en días (para el badge "Válido por N días"). */
  validityDays?: number | null;
  /** Etiqueta legible de la moneda (ej. "Soles (S/.)"). */
  currencyLabel?: string | null;
  /** Cláusulas legales incluidas en el documento. */
  clauses?: QuoteClauseResponse[];
  /** TODAS las firmas (APPROVER y CLIENT), ordenadas por fecha desc. */
  signatures?: QuoteSignatureView[];
  /** Hash SHA-256 del documento (null si aún no se firmó). */
  documentHash?: string | null;
  // ---- Condiciones comerciales (V74) ----
  paymentType?: QuotePaymentType | null;
  /** Etiqueta legible de la forma de pago ('Contado' | 'Crédito'). */
  paymentTypeLabel?: string | null;
  creditTermDays?: number | null;
  advancePercent?: number | null;
  /** Tasa de interés / financiamiento en porcentaje (0–100). null o 0 = sin interés. */
  interestRatePercent?: number | null;
  deliveryTime?: string | null;
  warranty?: string | null;
  deliveryPlace?: string | null;
  exchangeRate?: number | null;
  // ---- Moneda de conversión del tipo de cambio (V188) ----
  /** Código de la moneda de conversión (ej. 'USD'). null = tasa tecleada a mano. */
  exchangeRateCurrencyCode?: string | null;
  /** Nombre de la moneda de conversión (ej. 'Dólar Estadounidense'). */
  exchangeRateCurrencyName?: string | null;
  /** Símbolo de la moneda de conversión (ej. '$'). */
  exchangeRateCurrencySymbol?: string | null;
  /** Nombre de la lista de precios ligada a la cotización (V75). null si no tiene. */
  priceListName?: string | null;
  // ---- Opciones de presentación del documento (V76) ----
  /** Agrupar los ítems por categoría en el PDF/portal. */
  groupByCategory?: boolean;
  /** Mostrar la columna de códigos internos en el documento. */
  showItemCodes?: boolean;
  /** Permitir edición de precios en el PDF (preferencia almacenada). */
  allowPriceEdit?: boolean;
  items: QuoteItemResponse[];
}

// ---- Rechazo desde el portal público ----

/** Motivos catalogados de rechazo (portal público). */
export type QuoteRejectionReason =
  | 'PRECIO'
  | 'PLAZOS'
  | 'ELIGIO_OTRA_OPCION'
  | 'YA_NO_NECESARIO'
  | 'FALTA_FUNCIONALIDAD'
  | 'OTRO';

/** Etiquetas legibles en español para cada motivo de rechazo. */
export const QUOTE_REJECTION_REASON_LABELS: Record<QuoteRejectionReason, string> = {
  PRECIO: 'El precio no se ajusta',
  PLAZOS: 'Los plazos no funcionan',
  ELIGIO_OTRA_OPCION: 'Elegimos otra opción',
  YA_NO_NECESARIO: 'Ya no lo necesitamos',
  FALTA_FUNCIONALIDAD: 'Falta funcionalidad',
  OTRO: 'Otro motivo'
};

/** Devuelve la etiqueta legible de un motivo de rechazo (o el valor crudo si no está catalogado). */
export function quoteRejectionReasonLabel(reason?: string | null): string {
  if (!reason) return '';
  return QUOTE_REJECTION_REASON_LABELS[reason as QuoteRejectionReason] || reason;
}

// POST /api/quotes/public/{token}/reject — body
export interface QuotePublicRejectRequest {
  reason: QuoteRejectionReason;
  /** Opcional, máx 2000 caracteres */
  comment?: string;
  /** Opcional, máx 200 caracteres */
  rejectorName?: string;
  /** Opcional, máx 255 caracteres */
  rejectorEmail?: string;
}

// POST /api/quotes/public/{token}/reject — respuesta
export interface QuotePublicRejectResponse {
  /** Estado resultante, ej. "Rechazada" */
  status: string;
  quoteNumber: string;
}

/**
 * Construye la URL pública del frontend (hash routing) para compartir una cotización por token.
 * Conserva el path donde está servido el SPA (todo lo anterior al '#') y le agrega la ruta pública.
 */
export function buildPublicQuoteUrl(token: string): string {
  const base = window.location.href.split('#')[0];
  return `${base}#/cotizacion/${token}`;
}

// Sign request for public portal
export interface QuoteSignRequest {
  signatoryName: string;
  signatoryEmail?: string;
  signatureData?: string;
  signatureType: 'drawn' | 'typed' | 'electronic';
}

// Sign response
export interface QuoteSignatureResponse {
  id: number;
  idQuote: number;
  signatoryName: string;
  signatoryEmail?: string;
  signatoryIp?: string;
  signatoryDevice?: string;
  signatureData?: string;
  signatureType?: string;
  isValid: boolean;
  /** SHA-256 (hex, 64 chars) del documento aceptado al momento de firmar. */
  documentHash?: string;
  datetimeSigned: string;
  datetimeCreated: string;
}

// Audit history entry
export interface QuoteHistoryEntry {
  timestamp: string;
  operationType: string;
  operationSubtype: string;
  username: string;
  description: string;
  newValues?: string;
  ipAddress?: string;
}

// Version history entry — GET /api/quotes/{id}/versions
// Toda la familia comparte el mismo quoteNumber; isCurrent marca la versión vigente.
export interface QuoteVersionResponse {
  id: number;
  quoteNumber: string;
  version: number;
  statusId: number;
  statusName: string;
  versionNotes?: string;
  userCreatedId?: number;
  /** Nombre de quien creó la versión (nuevo en el contrato; nullable en versiones antiguas). */
  userCreatedName?: string | null;
  datetimeCreated: string;
  datetimeUpdated?: string;
  isCurrent: boolean;
}

// ---- Envío por correo (#28) ----

// Destinatario de correo (TO o CC)
export interface EmailRecipient {
  email: string;
  name?: string;
}

// POST /api/quotes/{id}/send-email — body. Todo opcional: body vacío usa el
// contacto principal de la cotización / del negocio asociado.
export interface SendQuoteEmailRequest {
  recipients?: EmailRecipient[];
  cc?: EmailRecipient[];
  /** Máx 2000 caracteres */
  personalMessage?: string;
}

// Respuesta del envío. quote trae publicToken/publicUrl/publicPdfUrl RECIÉN emitidos
// (se revelan solo aquí; el enlace anterior queda invalidado). emailMessageId es el
// id del mensaje encolado en el outbox (el envío real es asíncrono).
export interface SendQuoteEmailResponse {
  quote: QuoteResponse;
  emailMessageId: number;
}

// ---- Firma del responsable (V74) ----

// POST /api/quotes/{id}/responsible-sign — body OPCIONAL. Si viene signatureData usa esa firma;
// si el body va vacío, el backend usa la firma registrada del quote_approver del usuario actual.
export interface ResponsibleSignRequest {
  signatureData?: string;
  signatureType?: 'drawn' | 'typed' | 'uploaded';
}

// Metrics response
export interface QuoteMetricsResponse {
  totalQuotes: number;
  acceptedCount: number;
  rejectedCount: number;
  acceptanceRatePct: number;
  totalQuotedAmount: number;
  totalWonAmount: number;
  avgDaysToClose: number;
  expiringIn7Days: number;
  avgVersionCount: number;
}
